// The steps an institute has to complete before a student can take a test,
// in dependency order. Each is derived from data the console already loads,
// so the checklist never needs its own endpoint.

export const SETUP_STEPS = [
  {
    key: 'exam',
    title: 'Enrol in an exam',
    description: 'Pick the exam your students are preparing for. Batches and tests are built on it.',
    to: '/exam-library',
    action: 'Open exam library',
  },
  {
    key: 'batch',
    title: 'Create a batch',
    description: 'A batch groups students and sets the exam they prepare for.',
    to: '/batches',
    action: 'Create a batch',
  },
  {
    key: 'students',
    title: 'Add students',
    description: 'Invite by email, or share your join code and approve requests.',
    to: '/students',
    action: 'Add students',
  },
  {
    key: 'quiz',
    title: 'Launch your first test',
    description: 'Quick launch picks questions for you; the screener lets you choose topics.',
    to: '/manage-quiz',
    action: 'Launch a test',
  },
];

/**
 * Which setup steps are done.
 *
 * @param {object} data
 * @param {Array}  data.exams     enrolled exams
 * @param {Array}  data.batches
 * @param {Array}  data.students  includes pending ones
 * @param {Array}  data.quizzes
 */
export function computeSetupProgress({ exams = [], batches = [], students = [], quizzes = [] } = {}) {
  const done = {
    exam: exams.length > 0,
    batch: batches.length > 0,
    students: students.length > 0,
    quiz: quizzes.length > 0,
  };

  const steps = SETUP_STEPS.map((step, index) => {
    // A step is only actionable once everything before it is done, which is
    // exactly the ordering the pages themselves enforce.
    const blocked = SETUP_STEPS.slice(0, index).some((prev) => !done[prev.key]);
    return { ...step, done: done[step.key], blocked };
  });

  const completed = steps.filter((s) => s.done).length;
  const next = steps.find((s) => !s.done) || null;

  return {
    steps,
    completed,
    total: steps.length,
    isComplete: completed === steps.length,
    next,
  };
}

/** Whether the dashboard should still lead with setup rather than analytics. */
export function shouldShowSetup(progress) {
  return Boolean(progress) && !progress.isComplete;
}
